import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, Database, Cpu, HardDrive, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';

const MONITORING_REFRESH_INTERVAL_MS = 30000; // 30 seconds

const MonitoringDashboard = () => {
  const [healthData, setHealthData] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchMonitoringData = async () => {
      if (!BACKEND_URL) return;
      
      try {
        const [healthRes, metricsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/health/detailed`),
          fetch(`${BACKEND_URL}/api/metrics`)
        ]);

        const health = await healthRes.json();
        const metrics = await metricsRes.json();

        setHealthData(health);
        setMetricsData(metrics);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMonitoringData();
    const interval = setInterval(fetchMonitoringData, MONITORING_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
    // BACKEND_URL and MONITORING_REFRESH_INTERVAL_MS are constants, setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BACKEND_URL]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-8 text-center">
            <Activity className="w-8 h-8 mx-auto mb-2 animate-pulse" />
            <p>Loading monitoring data...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
            <p className="text-red-600">Error loading monitoring data: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      healthy: { variant: 'default', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      degraded: { variant: 'secondary', className: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
      unhealthy: { variant: 'destructive', className: 'bg-red-100 text-red-800', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.unhealthy;
    const Icon = config.icon;

    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {status.toUpperCase()}
      </Badge>
    );
  };

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">System Monitoring Dashboard</h1>
        <p className="text-gray-600">Real-time health and performance metrics</p>
      </div>

      {/* Overall Status */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>System Status</CardTitle>
            {getStatusBadge(healthData?.status)}
          </div>
          <CardDescription>
            Last updated: {new Date(healthData?.timestamp).toLocaleString()}
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Database Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center">
              <Database className="w-4 h-4 mr-2" />
              Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getStatusBadge(healthData?.checks?.database?.status)}
            <p className="text-xs text-gray-600 mt-2">
              {healthData?.checks?.database?.message}
            </p>
          </CardContent>
        </Card>

        {/* CPU Usage */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center">
              <Cpu className="w-4 h-4 mr-2" />
              CPU Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {healthData?.checks?.system?.cpu_usage_percent}%
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${
                  healthData?.checks?.system?.cpu_usage_percent > 80 ? 'bg-red-600' :
                  healthData?.checks?.system?.cpu_usage_percent > 60 ? 'bg-yellow-600' : 'bg-green-600'
                }`}
                style={{ width: `${healthData?.checks?.system?.cpu_usage_percent}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Memory Usage */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center">
              <Activity className="w-4 h-4 mr-2" />
              Memory Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {healthData?.checks?.system?.memory_usage_percent}%
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {healthData?.checks?.system?.memory_available_mb} MB available
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${
                  healthData?.checks?.system?.memory_usage_percent > 80 ? 'bg-red-600' :
                  healthData?.checks?.system?.memory_usage_percent > 60 ? 'bg-yellow-600' : 'bg-green-600'
                }`}
                style={{ width: `${healthData?.checks?.system?.memory_usage_percent}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Disk Usage */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center">
              <HardDrive className="w-4 h-4 mr-2" />
              Disk Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {healthData?.checks?.system?.disk_usage_percent}%
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {healthData?.checks?.system?.disk_free_gb} GB free
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${
                  healthData?.checks?.system?.disk_usage_percent > 80 ? 'bg-red-600' :
                  healthData?.checks?.system?.disk_usage_percent > 60 ? 'bg-yellow-600' : 'bg-green-600'
                }`}
                style={{ width: `${healthData?.checks?.system?.disk_usage_percent}%` }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Application Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Application Metrics</CardTitle>
          <CardDescription>Performance and usage statistics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center mb-2">
                <Clock className="w-4 h-4 mr-2 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Uptime</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatUptime(metricsData?.system?.uptime_seconds || 0)}
              </p>
            </div>
            <div>
              <div className="flex items-center mb-2">
                <Database className="w-4 h-4 mr-2 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Database Size</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {metricsData?.database?.data_size_mb} MB
              </p>
            </div>
            <div>
              <div className="flex items-center mb-2">
                <Activity className="w-4 h-4 mr-2 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Collections</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {metricsData?.database?.collections || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MonitoringDashboard;
